/**
 * File: SectionGlance.tsx
 * Path: apps/admin/src/routes/menu/SectionGlance.tsx
 * Description: The one or two lines at the top of a section's own page
 * (phones): what needs attention in CRM, drafts waiting in CMS. Accounting
 * has none; its first link is the overview itself.
 */

import React from "react";
import { Link } from "react-router-dom";
import { ADMIN_PATHS } from "@idk/config";
import { ArrowRight } from "lucide-react";
import { useCrmGlanceController } from "@/features/menu/hooks/useCrmGlanceController";
import { useCmsGlanceController } from "@/features/menu/hooks/useCmsGlanceController";

const glanceClass =
  "flex min-h-14 flex-wrap items-center justify-between gap-stack-sm border border-border/60 bg-card p-gutter-sm text-sm hover:border-primary/40";

const GlanceLink: React.FC<{ to: string; label: string; children: React.ReactNode }> = ({
  to,
  label,
  children,
}) => (
  <Link to={to} className={glanceClass}>
    <span>{children}</span>
    <span className="inline-flex items-center gap-stack-xs text-xs font-bold uppercase tracking-wider text-primary">
      {label}
      <ArrowRight className="h-4 w-4" aria-hidden />
    </span>
  </Link>
);

const CrmGlance: React.FC = () => {
  const { text } = useCrmGlanceController();
  if (!text) return null;
  return (
    <GlanceLink to={ADMIN_PATHS.leads.root} label="Leads">
      <strong className="font-bold text-foreground">{text.headline}</strong>
      <span className="text-muted-foreground">
        {" "}
        · {text.detail}
      </span>
    </GlanceLink>
  );
};

const CmsGlance: React.FC = () => {
  const { loaded, drafts, text } = useCmsGlanceController();
  if (!loaded) return null;
  if (drafts === 0) {
    return (
      <p className={glanceClass}>
        <strong className="font-bold text-foreground">No drafts waiting</strong>
      </p>
    );
  }
  return (
    <p className={glanceClass}>
      <span>
        <strong className="font-bold text-foreground">Drafts waiting</strong>
        <span className="text-muted-foreground">
          {" "}
          · {text}
        </span>
      </span>
    </p>
  );
};

export const SectionGlance: React.FC<{ hub: string; canSeeLeads: boolean }> = ({
  hub,
  canSeeLeads,
}) => {
  if (hub === ADMIN_PATHS.menu.crm && canSeeLeads) return <CrmGlance />;
  if (hub === ADMIN_PATHS.menu.cms) return <CmsGlance />;
  return null;
};
